import { Callout } from '@/components/Callout'
import { GarageHeader } from '@/components/GarageHeader'
import { MapStatic } from '@/components/MapStatic'
import { ProjectCard } from '@/components/ProjectCard'
import { laravelGraphqlEndpoint } from '@/globals/globals'
import { parseJSONinObject } from '@/lib/parseJSONinObject'
import { Garage } from '@/types/garage'
import { Project } from '@/types/project'
import {
  Box,
  Container,
  Flex,
  Heading,
  Link,
  SimpleGrid,
  Text,
} from '@chakra-ui/react'
import { gql, request } from 'graphql-request'
import { NextSeo } from 'next-seo'
import NextLink from 'next/link'
import { FaInstagram, FaTiktok, FaTwitter, FaYoutube } from 'react-icons/fa'

export async function getStaticPaths() {
  return { paths: [], fallback: true }
}

export async function getStaticProps({ params }: any) {
  const post = await request(
    laravelGraphqlEndpoint,
    gql`
      query ($slug: String!) {
        garage: garageBySlug(slug: $slug) {
          city
          country
          instagram
          is_business
          lat
          lng
          logo
          public
          state
          tiktok
          title
          twitter
          uuid
          slug
          youtube
          user {
            avatar
            projects {
              images
              published
              slug
              title
            }
          }
        }
      }
    `,
    { slug: params.slug }
  )

  let postData = post

  if (typeof post.garage.user.projects !== 'undefined') {
    postData.garage.user.projects = post.garage.user.projects.map(
      (project: Project) => parseJSONinObject(project)
    )
  }

  let data = post.garage

  // Only one image per project
  if (typeof data.user.projects !== 'undefined') {
    data.user.projects = data.user.projects
      .map((project: Project) => {
        if (
          typeof project.images !== 'undefined' &&
          project.images?.length > 0
        ) {
          project.images = project.images[0]
            ? [project.images[0]]
            : [{ url: '' }]
        }
        return project.published ? project : null
      })
      .filter((project: Project) => project !== null)
  }

  return { props: data, revalidate: 10 }
}

export default function Post(garage: Garage) {
  const projectCount = garage.user?.projects?.length || 0
  const fullColumns: {} | number = { base: 1, md: 2, lg: 3 }
  let projectColumns = fullColumns

  switch (projectCount) {
    case 1: {
      projectColumns = 1
      break
    }
    case 2: {
      projectColumns = { base: 1, md: 2 }
      break
    }
  }

  return (
    <>
      <NextSeo title={garage.title} />
      <GarageHeader garage={garage} />
      {garage.is_business && (
        <Box textAlign='center' p={12} backgroundColor='gray.200' fontSize='xl'>
          Need help on a project? {garage.title} is open for business,{' '}
          <NextLink
            href={`/nation/garages/${garage.slug}/contact`}
            passHref
            legacyBehavior>
            <Link>contact ›</Link>
          </NextLink>
        </Box>
      )}
      <Container maxWidth={projectCount > 1 ? '8xl' : '4xl'}>
        <Heading as='h3' pt={10} px={10} textAlign='center'>
          Projects
        </Heading>
        {garage.user?.projects && garage.user.projects.length > 0 ? (
          <SimpleGrid columns={projectColumns} spacing={10} p={10}>
            {garage.user?.projects.map((project: any) => (
              <ProjectCard project={project} key={project.uuid} />
            ))}
          </SimpleGrid>
        ) : (
          <Container py={12} textAlign='center'>
            <Text size='lg'>
              {garage.title} has not published a project yet.
            </Text>
          </Container>
        )}
      </Container>
      {(garage.instagram ||
        garage.youtube ||
        garage.tiktok ||
        garage.twitter) && (
        <Flex justifyContent='center' gap={8} fontSize='2xl' pt={8}>
          {garage.instagram && (
            <Link
              href={'https://www.instagram.com/' + garage.instagram}
              _hover={{ color: 'red.400' }}
              target='_blank'>
              <FaInstagram />
            </Link>
          )}
          {garage.youtube && (
            <Link
              href={garage.youtube}
              _hover={{ color: 'red.400' }}
              target='_blank'>
              <FaYoutube />
            </Link>
          )}
          {garage.tiktok && (
            <Link
              href={'https://www.tiktok.com/@' + garage.tiktok}
              _hover={{ color: 'red.400' }}
              target='_blank'>
              <FaTiktok />
            </Link>
          )}
          {garage.twitter && (
            <Link
              href={garage.twitter}
              _hover={{ color: 'red.400' }}
              target='_blank'>
              <FaTwitter />
            </Link>
          )}
        </Flex>
      )}
      {garage.is_business || garage.instagram ? (
        <Container
          py={24}
          maxWidth={garage.is_business && garage.instagram ? '4xl' : 'xl'}>
          <SimpleGrid
            spacing={6}
            columns={garage.is_business && garage.instagram ? 2 : 1}
            mb={6}>
            {garage.is_business && (
              <Callout
                href={`https://www.google.ca/search?q=${garage.title}`}
                title='Google Reviews'
              />
            )}
            {garage.instagram && (
              <Callout
                href={`https://instagram.com/${garage.instagram}`}
                title='Instagram Feed'
              />
            )}
          </SimpleGrid>
        </Container>
      ) : null}
      {garage?.lat && garage?.lng && (
        <Box py={12}>
          {garage.lng && garage.lat && (
            <MapStatic
              linkText='View Garages Map'
              linkUrl='/nation/map/garages'
              lat={Number(garage.lat)}
              lng={Number(garage.lng)}
            />
          )}
        </Box>
      )}
      <Container pb={12}>
        <Callout href='/nation/projects' title='More Projects' />
      </Container>
    </>
  )
}
